import React, {type JSX, useCallback, useEffect, useState} from "react";
import {useNotificacionStore} from "@rfacil/shared/hooks";

/**
 * @interface NotificationModalProps
 * @description Define las propiedades del componente de notificación modal.
 */
interface NotificationModalProps {
	notificacion: {
		id: number;
		mensaje: string;
		gravedad: "success" | "error" | "info" | "warning";
	};
	onClose: () => void;
}

/**
 * @component NotificationModal
 * @description Componente individual de notificación tipo card con diseño moderno y elegante.
 * Presenta un estilo limpio con fondo blanco, borde de color y animaciones suaves.
 * Incluye efectos visuales llamativos para captar la atención del usuario.
 * @param {NotificationModalProps} props Las propiedades del componente.
 * @returns {JSX.Element | null} El componente card de notificación renderizado o nulo.
 */
const NotificationModal: React.FC<NotificationModalProps> = ({
	notificacion,
	onClose,
}) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [progressWidth, setProgressWidth] = useState(100);
	const [isPulsing, setIsPulsing] = useState(false);

	const handleClose = useCallback(() => {
		setIsClosing(true);
		setTimeout(() => {
			onClose();
		}, 100);
	}, [onClose]);

	useEffect(() => {
		// Resetear estados cuando cambia la notificación
		setIsVisible(false);
		setIsClosing(false);
		setProgressWidth(100);
		setIsPulsing(false);

		// Animación de entrada
		const visibleTimer = setTimeout(() => setIsVisible(true), 10);

		// Efecto de pulso inicial para llamar la atención
		setIsPulsing(true);
		const pulseTimer = setTimeout(() => setIsPulsing(false), 1000);

		// Animación de la barra de progreso
		const progressInterval = setInterval(() => {
			setProgressWidth((prev) => {
				if (prev <= 0) {
					clearInterval(progressInterval);
					return 0;
				}
				return prev - 2; // Reduce 2% cada 100ms (5 segundos total)
			});
		}, 100);

		// Cierre automático después de 5 segundos
		const autoCloseTimer = setTimeout(() => {
			handleClose();
		}, 5000);

		return () => {
			clearTimeout(autoCloseTimer);
			clearTimeout(pulseTimer);
			clearTimeout(visibleTimer);
			clearInterval(progressInterval);
		};
	}, [notificacion.id, handleClose]);

	// Configuración de colores y iconos según la gravedad usando variables CSS personalizadas
	const getNotificationConfig = () => {
		switch (notificacion.gravedad) {
			case "success":
				return {
					accentColor: "var(--color-success-500)",
					iconColor: "var(--color-success-600)",
					iconBg: "var(--color-success-50)",
					textColor: "var(--color-gray-800)",
					titleColor: "var(--color-success-700)",
					progressColor: "var(--color-success-500)",
					icon: (
						<svg
							className="w-6 h-6 sm:w-8 sm:h-8"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2.5}
								d="M5 13l4 4L19 7"
							/>
						</svg>
					),
					title: "Éxito",
				};
			case "error":
				return {
					accentColor: "var(--color-error-500)",
					iconColor: "var(--color-error-600)",
					iconBg: "var(--color-error-50)",
					textColor: "var(--color-gray-800)",
					titleColor: "var(--color-error-700)",
					progressColor: "var(--color-error-500)",
					icon: (
						<svg
							className="w-6 h-6 sm:w-8 sm:h-8"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2.5}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					),
					title: "Error",
				};
			case "warning":
				return {
					accentColor: "var(--color-warning-500)",
					iconColor: "var(--color-warning-600)",
					iconBg: "var(--color-warning-50)",
					textColor: "var(--color-gray-800)",
					titleColor: "var(--color-warning-700)",
					progressColor: "var(--color-warning-500)",
					icon: (
						<svg
							className="w-6 h-6 sm:w-8 sm:h-8"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2.5}
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
							/>
						</svg>
					),
					title: "Advertencia",
				};
			case "info":
			default:
				return {
					accentColor: "var(--color-info-500)",
					iconColor: "var(--color-info-600)",
					iconBg: "var(--color-info-50)",
					textColor: "var(--color-gray-800)",
					titleColor: "var(--color-info-700)",
					progressColor: "var(--color-info-500)",
					icon: (
						<svg
							className="w-6 h-6 sm:w-8 sm:h-8"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2.5}
								d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					),
					title: "Información",
				};
		}
	};

	const config = getNotificationConfig();

	return (
		<div
			className={`fixed bottom-4 left-4 right-4 sm:right-auto sm:left-4 sm:max-w-md w-auto sm:w-full transition-all duration-500 ease-out ${
				isVisible && !isClosing
					? "opacity-100 translate-y-0 translate-x-0"
					: "opacity-0 translate-y-8 translate-x-[-20px]"
			}`}
			// Por encima de MUI (~1300), overlays a pantalla completa del host (~10050) y diálogos (~12000)
			style={{zIndex: 13000}}
		>
			{/* Card de notificación con diseño moderno */}
			<div
				className={`relative bg-background-primary rounded-2xl border-2 shadow-2xl overflow-hidden transform transition-all duration-500 ${
					isPulsing ? "ring-4" : ""
				}`}
				style={{
					borderColor: config.accentColor,
					boxShadow: isVisible && !isClosing
						? `0 20px 60px -15px rgba(0, 0, 0, 0.3), 0 0 0 1px ${config.accentColor}20, 0 0 40px ${config.accentColor}15${
								isPulsing ? `, 0 0 0 4px ${config.accentColor}40` : ""
						  }`
						: "none",
				}}
			>
				{/* Barra de progreso animada en la parte superior */}
				<div className="h-1 bg-gray-100 relative overflow-hidden">
					<div
						className="h-full transition-all duration-100 ease-linear absolute top-0 left-0"
						style={{
							width: isClosing ? "0%" : `${progressWidth}%`,
							backgroundColor: config.progressColor,
							boxShadow: `0 2px 8px ${config.progressColor}40`,
						}}
					>
						{/* Efecto de brillo deslizante */}
						<div
							className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
							style={{
								animation: "slideShine 1.5s ease-in-out infinite",
							}}
						/>
					</div>
				</div>

				{/* Contenido principal */}
				<div className="p-3 sm:p-5">
					<div className="flex items-start gap-3 sm:gap-4">
						{/* Icono grande con fondo suave */}
						<div
							className={`flex-shrink-0 rounded-xl p-2 sm:p-3 transition-all duration-500 ${
								isPulsing ? "scale-110 rotate-3" : "scale-100 rotate-0"
							}`}
							style={{
								backgroundColor: config.iconBg,
								color: config.iconColor,
							}}
						>
							{config.icon}
						</div>

						{/* Contenido del mensaje */}
						<div className="flex-1 min-w-0 pt-0.5 sm:pt-1">
							<h3
								className={`text-base sm:text-lg font-bold mb-1 sm:mb-1.5 transition-all duration-300 ${
									isPulsing ? "scale-105" : "scale-100"
								}`}
								style={{ color: config.titleColor }}
							>
								{config.title}
							</h3>
							<div
								className="text-xs sm:text-sm leading-relaxed"
								style={{ color: config.textColor }}
								dangerouslySetInnerHTML={{__html: notificacion.mensaje}}
							/>
						</div>
					</div>
				</div>

				{/* Barra de progreso animada en la parte inferior */}
				<div className="h-1 bg-gray-100 relative overflow-hidden">
					<div
						className="h-full transition-all duration-100 ease-linear absolute top-0 left-0"
						style={{
							width: isClosing ? "0%" : `${progressWidth}%`,
							backgroundColor: config.progressColor,
							boxShadow: `0 2px 8px ${config.progressColor}40`,
						}}
					>
						{/* Efecto de brillo deslizante */}
						<div
							className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
							style={{
								animation: "slideShine 1.5s ease-in-out infinite",
							}}
						/>
					</div>
				</div>
			</div>

			{/* Estilos CSS para animaciones */}
			<style>{`
				@keyframes slideShine {
					0% {
						transform: translateX(-100%) skewX(-15deg);
					}
					100% {
						transform: translateX(200%) skewX(-15deg);
					}
				}
			`}</style>
		</div>
	);
};

/**
 * Un componente global que muestra notificaciones tipo card al usuario.
 * Se integra con un store de manejo de estado global (`useNotificacionStore`) para mostrar mensajes.
 * Las notificaciones aparecen como cards en la esquina inferior izquierda con diseño moderno,
 * elegante y animaciones llamativas para captar la atención del usuario.
 * Se cierran automáticamente después de 5 segundos.
 *
 * @component
 * @returns {JSX.Element} Componente que renderiza las notificaciones como cards.
 */
const CustomSnackbar = (): JSX.Element => {
	const {notificaciones, closeNotification} = useNotificacionStore();

	// Mostrar solo la última notificación si hay múltiples
	const ultimaNotificacion =
		notificaciones.length > 0 ? notificaciones[notificaciones.length - 1] : null;

	if (!ultimaNotificacion) return <></>;

	return (
		<NotificationModal
			key={ultimaNotificacion.id}
			notificacion={ultimaNotificacion}
			onClose={() => closeNotification(ultimaNotificacion.id)}
		/>
	);
};

export default CustomSnackbar;
